/**
 * Eingabefeld mit Beschriftung, Hinweis und Fehler.
 *
 * Übernommen aus `components/forms/Input.jsx` des Design-System-Projekts. Der
 * Rahmen färbt sich beim Fokus grün und beim Fehler rot — beides Marken- bzw.
 * Statusfarben, keine Datenfarben.
 *
 * Die Beschriftung ist ein echtes `<label for>`, kein daneben stehender Text:
 * Sonst vergrößert ein Klick darauf nicht das Berührziel, und ein Screenreader
 * liest das Feld ohne Namen vor.
 */
import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { fontWeight } from "../tokens.js";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  readonly label: string;
  /** Erklärung unter dem Feld. Wird vom Fehler verdrängt, wenn einer vorliegt. */
  readonly hint?: string;
  readonly error?: string;
  readonly size?: "sm" | "md" | "lg";
  readonly trailing?: ReactNode;
  /**
   * Beschriftung nur für Hilfsmittel sichtbar — wie bei `Select`. Für Felder,
   * deren Beschriftung schon daneben steht (die Zeilen der Einstellungen).
   */
  readonly labelHidden?: boolean;
}

const HEIGHTS = { sm: 32, md: 38, lg: 44 } as const;

export function Input({
  label,
  hint,
  error,
  size = "md",
  trailing,
  labelHidden = false,
  disabled = false,
  id,
  style,
  ...rest
}: InputProps) {
  const [focused, setFocused] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedById = hint !== undefined || error !== undefined ? `${inputId}-note` : undefined;

  const borderColor = error
    ? "var(--hm-danger)"
    : focused
      ? "var(--hm-green-500)"
      : "var(--border-default)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      <label
        htmlFor={inputId}
        style={
          labelHidden
            ? // Sichtbar für Sprachausgaben, unsichtbar auf dem Bildschirm.
              { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }
            : { fontSize: 13, fontWeight: fontWeight.semibold, color: "var(--text-body)" }
        }
      >
        {label}
      </label>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: HEIGHTS[size],
          padding: "0 12px",
          background: disabled ? "var(--surface-sunken)" : "var(--surface-card)",
          border: `1px solid ${borderColor}`,
          borderRadius: "var(--radius-md)",
          boxShadow: focused ? "0 0 0 var(--ring-width) var(--focus-ring)" : "var(--shadow-xs)",
          transition: "border-color .15s ease, box-shadow .15s ease",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          id={inputId}
          disabled={disabled}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={describedById}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            /**
             * Der Fokusring sitzt am Rahmen ringsum, nicht am nackten `<input>`
             * — sonst lägen zwei Ringe ineinander.
             */
            outline: "none",
            background: "transparent",
            fontFamily: "var(--font-body)",
            fontSize: size === "sm" ? "var(--field-font-sm)" : "var(--field-font-md)",
            color: "var(--text-strong)",
          }}
          {...rest}
        />
        {trailing !== undefined && (
          <span style={{ display: "inline-flex", color: "var(--text-faint)" }}>{trailing}</span>
        )}
      </div>

      {(error ?? hint) !== undefined && (
        <span
          id={describedById}
          style={{ fontSize: 12, color: error ? "var(--hm-danger)" : "var(--text-muted)" }}
        >
          {error ?? hint}
        </span>
      )}
    </div>
  );
}
